import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { find, propEq, filter } from 'ramda';

import { UnifiedInitialOffersResult } from 'hooks/requests/unifiedInitialOffers';
import { selectTenant, OfferTypesType, isOfferConfigured } from 'state/tenant';

import { FinanceResult } from 'state/financeOffers';
import useAnalyticVariables, {
  OfferWidgetType,
} from 'hooks/useAnalyticVariables';
import { FirstOffersEnum } from 'hooks/useQueryParameters';
import { VirtualLookupResult } from 'hooks/requests/virtualLookup';

type HasInitialOfferHook = {
  (): (
    initialFinanceOffers: UnifiedInitialOffersResult | undefined,
    firstOffers: FirstOffersEnum | undefined,
  ) => OfferTypesType[];
};

type HasOfferHook = {
  (): (
    initialFinanceOffers: FinanceResult | undefined,
    prequalOffer: VirtualLookupResult | undefined,
    firstOffers: FirstOffersEnum | undefined,
    hasExistingAccountQueryParam: boolean,
  ) => OfferTypesType[];
};

type WidgetState = Partial<Record<OfferWidgetType, boolean>>;

export const useHasInitialOffers: HasInitialOfferHook = () => {
  const { bread } = useSelector(selectTenant);
  const { setAnalyticsVariables } = useAnalyticVariables();

  if (!bread) {
    throw new Error('Invalid tenant');
  }

  const getHasInitialOffers = useCallback(
    (
      initialFinanceOffers: UnifiedInitialOffersResult | undefined,
      firstOffers: FirstOffersEnum | undefined,
    ) => {
      // const hasPrequal = ;
      const widgetState: WidgetState = {
        installments:
          isOfferConfigured('installments', bread) &&
          !!(
            initialFinanceOffers &&
            find(
              propEq('paymentProductType', 'INSTALLMENTS'),
              initialFinanceOffers.paymentProducts || [],
            )
          ),
        splitpay:
          isOfferConfigured('splitpay', bread) &&
          !!(
            initialFinanceOffers &&
            find(
              propEq('paymentProductType', 'SPLITPAY'),
              initialFinanceOffers.paymentProducts || [],
            )
          ),
        prequalOffer: isOfferConfigured('prequalification', bread),
      };

      const hasInitialOffers: OfferTypesType[] = [];

      // The order below is important and should be preserved
      if (widgetState.installments) hasInitialOffers.push('installments');
      if (widgetState.notQualified) hasInitialOffers.push('splitpay');
      if (widgetState.prequalOffer) hasInitialOffers.push('prequalification');

      const initialOffersShown = filter(
        key => Boolean(widgetState[key]),
        Object.keys(widgetState) as OfferWidgetType[],
      );
      setAnalyticsVariables({
        initialOffersShown: initialOffersShown.join(','),
      });

      // Correct prescedence for initialOfferOnTop is to check if there is only 1 offer,
      // then check the firstOffers query param, then check the first entry of hasInitialOffers
      if (hasInitialOffers.length === 1) {
        const onTop =
          hasInitialOffers[0] === 'prequalification'
            ? 'prequalOffer'
            : hasInitialOffers[0];
        setAnalyticsVariables({
          initialOfferOnTop: onTop,
        });
      } else if (firstOffers && hasInitialOffers.includes(firstOffers)) {
        const onTop =
          firstOffers === 'prequalification' ? 'prequalOffer' : firstOffers;
        setAnalyticsVariables({
          initialOfferOnTop: onTop,
        });
      } else {
        const onTop =
          hasInitialOffers[0] === 'prequalification'
            ? 'prequalOffer'
            : hasInitialOffers[0];
        setAnalyticsVariables({
          initialOfferOnTop: onTop,
        });
      }

      return hasInitialOffers;
    },
    [bread, setAnalyticsVariables],
  );

  return getHasInitialOffers;
};

export const useHasOffers: HasOfferHook = () => {
  const { bread } = useSelector(selectTenant);
  const { setAnalyticsVariables } = useAnalyticVariables();

  if (!bread) {
    throw new Error('Invalid tenant');
  }

  const getHasOffers = useCallback(
    (
      unifiedOffers: FinanceResult | undefined,
      prequalOffer: VirtualLookupResult | undefined,
      firstOffers: FirstOffersEnum | undefined,
      hasExistingAccountQueryParam: boolean,
    ) => {
      const hasPrequal = isOfferConfigured('prequalification', bread);

      const hasInstallments =
        isOfferConfigured('installments', bread) &&
        !!(
          unifiedOffers &&
          find(
            propEq('paymentProductType', 'SPLITPAY'),
            unifiedOffers.offers || [],
          )
        );

      const hasSplitpay =
        isOfferConfigured('splitpay', bread) &&
        !!(
          unifiedOffers &&
          find(
            propEq('paymentProductType', 'INSTALLMENTS'),
            unifiedOffers.offers || [],
          )
        );

      const hasOffers: OfferTypesType[] = [];

      let prequalWidgetType: OfferWidgetType = 'prequalFallback';

      // The order below is important and should be preserved
      if (hasInstallments) hasOffers.push('installments');
      if (hasSplitpay) hasOffers.push('splitpay');
      if (hasPrequal) {
        hasOffers.push('prequalification');
        if (
          (prequalOffer && prequalOffer.hasExistingAccount) ||
          hasExistingAccountQueryParam
        ) {
          prequalWidgetType = 'prequalAccountFound';
        } else if (
          prequalOffer &&
          prequalOffer.prequalificationId &&
          !prequalOffer.isExpired
        ) {
          prequalWidgetType = 'prequalOffer';
        }
      }

      setAnalyticsVariables({
        offersShown: hasOffers
          .map(e => (e === 'prequalification' ? prequalWidgetType : e))
          .join(','),
      });

      // Correct prescedence for offerOnTop is to check if there is only 1 offer,
      // then check existing account, then check firstOffers query param,
      // then check the first entry of hasOffers
      if (hasOffers.length === 1) {
        const onTop =
          hasOffers[0] === 'prequalification'
            ? prequalWidgetType
            : hasOffers[0];
        setAnalyticsVariables({
          offerOnTop: onTop,
        });
      } else if (prequalWidgetType === 'prequalAccountFound') {
        setAnalyticsVariables({
          offerOnTop: 'prequalAccountFound',
        });
      } else if (firstOffers && hasOffers.includes(firstOffers)) {
        const onTop =
          firstOffers === 'prequalification' ? prequalWidgetType : firstOffers;
        setAnalyticsVariables({
          offerOnTop: onTop,
        });
      } else {
        const onTop =
          hasOffers[0] === 'prequalification'
            ? prequalWidgetType
            : hasOffers[0];
        setAnalyticsVariables({
          offerOnTop: onTop,
        });
      }

      return hasOffers;
    },
    [bread, setAnalyticsVariables],
  );

  return getHasOffers;
};
